import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, functions } from '../firebase';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CheckIcon } from '@heroicons/react/20/solid';
import { getStripeConfig } from '../config/stripe';
import { httpsCallable, getFunctions } from 'firebase/functions';

// Initialize Stripe with the appropriate key
const stripePromise = loadStripe(getStripeConfig().publishableKey);

const SUBSCRIPTION_PRICE = 2.99;

const premiumFeatures = [
  'Access to every golf course on our platform',
  'Access to future features like notifications and advanced filtering',
  'Cancel anytime'
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function UpgradeToPremiumForm() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const { currentUser } = useAuth();
  const [accessCode, setAccessCode] = useState('');
  const [codeStatus, setCodeStatus] = useState({ busy: false, error: '', done: false });
  const navigate = useNavigate();
  const location = useLocation();
  const startTrial = location.state?.startTrial || false;
  const stripe = useStripe();
  const elements = useElements();
  const [showConfirm, setShowConfirm] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const functions = getFunctions();
  const upgradeTrialToPremium = httpsCallable(functions, 'upgradeTrialToPremium');

  const handleSubmit = async (event) => {
    if (event) {
      event.preventDefault();
    }
    setError('');
    setLoading(true);

    if (!stripe || !elements) {
      setError('Stripe has not been initialized');
      setLoading(false);
      return;
    }

    try {
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: cardholderName,
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      console.log('Payment method created:', paymentMethod.id);

      // Call the Firebase function with explicit project ID and region
      const createSubscriptionFunction = httpsCallable(functions, 'createSubscription', {
        region: 'us-central1',
        projectId: 'tee-time-finder-2025-9b689'
      });
      const result = await createSubscriptionFunction({
        paymentMethodId: paymentMethod.id,
        isTrial: startTrial
      });
      const { subscriptionId, clientSecret } = result.data;

      console.log('Subscription created:', subscriptionId);

      if (clientSecret) {
        // Confirm the payment only if there is a clientSecret (not a trial)
        const { error: confirmError } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: paymentMethod.id,
          }
        );

        if (confirmError) {
          setError(confirmError.message);
          setLoading(false);
          return;
        }
      }
      // If it's a trial or payment succeeded, proceed
      
      // Track free trial starts
      if (startTrial && window.gtag) {
        window.gtag('event', 'begin_trial', {
          trial_type: 'premium_subscription',
          trial_duration: '7_days',
        });
      }
      
      navigate('/');
    } catch (error) {
      console.error('Subscription error:', error);
      // Log the error object for debugging
      console.log('Full error object:', error);
      // Try to handle both direct and nested error codes/messages
      const errorCode = error.code || error?.error?.code;
      const errorMessage = error.message || error?.error?.message;

      if (errorCode === 'failed-precondition' || (errorMessage && errorMessage.includes('already used a free trial'))) {
        setError('You have already used a free trial.');
      } else {
        setError(errorMessage || 'An error occurred while processing your payment');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeConfirm = async () => {
    setUpgradeLoading(true);
    setUpgradeError('');
    try {
      const result = await upgradeTrialToPremium();
      setUpgradeSuccess(true);
      
      // Track premium upgrade
      if (window.gtag) {
        window.gtag('event', 'upgrade_to_premium', {
          upgrade_type: 'trial_to_premium',
          subscription_price: 2.99,
        });
      }
      
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setUpgradeError(err.message || 'Failed to upgrade.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  if (currentUser?.isTrial && !startTrial) {
    return (
      <div className="relative isolate px-6 py-10 sm:py-14 lg:px-8">
        <div aria-hidden="true" className="absolute inset-x-0 -top-3 -z-10 transform-gpu overflow-hidden px-36 blur-3xl">
          <div
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
            className="mx-auto aspect-1155/678 w-[72.1875rem] bg-gradient-to-tr from-[#4ade80] to-[#22c55e] opacity-30"
          />
        </div>
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-base font-semibold text-slate-900">Upgrade Your Trial to Premium</h2>
          <p className="mt-2 text-5xl font-semibold tracking-tight text-gray-900 sm:text-6xl">
            Get Full Premium Access Now
          </p>
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-lg text-slate-900">
          Upgrade your trial to full premium access. Your trial will end and you'll be charged immediately.
        </p>
        <div className="mx-auto mt-16 max-w-4xl">
          <div className="bg-white rounded-3xl p-8 ring-1 ring-gray-900/10 shadow-lg sm:p-10">
            <h3 className="text-base font-semibold text-green-600 mb-6">Premium Features</h3>
            <ul className="space-y-4">
              {premiumFeatures.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <CheckIcon className="h-6 w-6 text-green-500 mr-2" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 text-center">
              {upgradeError && <div className="text-red-600 mb-4">{upgradeError}</div>}
              {upgradeSuccess ? (
                <div className="text-green-600 font-bold">Upgrade successful! Redirecting...</div>
              ) : (
                <button
                  className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold text-lg disabled:opacity-50 hover:bg-green-700 transition-colors"
                  onClick={handleUpgradeConfirm}
                  disabled={upgradeLoading}
                >
                  {upgradeLoading ? 'Upgrading...' : 'Upgrade Now'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleRedeemCode = async (e) => {
    e.preventDefault();
    if (!accessCode.trim()) return;
    setCodeStatus({ busy: true, error: '', done: false });
    try {
      const functions = getFunctions();
      const redeem = httpsCallable(functions, 'redeemAccessCode');
      const result = await redeem({ code: accessCode });
      if (result.data.success) {
        setCodeStatus({ busy: false, error: '', done: true });
        // Full reload so AuthContext re-reads premium status before routing.
        setTimeout(() => window.location.replace('/'), 1200);
      } else {
        setCodeStatus({ busy: false, error: result.data.error || 'That code is not valid.', done: false });
      }
    } catch (err) {
      setCodeStatus({ busy: false, error: err.message || 'Could not redeem that code.', done: false });
    }
  };

  return (
    <div className="relative isolate px-6 py-10 sm:py-14 lg:px-8">
      <div aria-hidden="true" className="absolute inset-x-0 -top-3 -z-10 transform-gpu overflow-hidden px-36 blur-3xl">
        <div
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
          className="mx-auto aspect-1155/678 w-[72.1875rem] bg-gradient-to-tr from-[#4ade80] to-[#22c55e] opacity-30"
        />
      </div>
      <div className="mx-auto max-w-4xl text-center">
        <h2 className="text-base font-semibold text-slate-900">
          {startTrial ? 'Start Your Free Trial' : 'Upgrade to Premium'}
        </h2>
        <p className="mt-2 text-5xl font-semibold tracking-tight text-gray-900 sm:text-6xl">
          {startTrial ? 'Try Premium Free for 7 Days' : 'Get access to premium features'}
        </p>
      </div>
      <p className="mx-auto mt-6 max-w-2xl text-center text-lg text-slate-900">
        {startTrial 
          ? 'Experience all premium features with our 7-day free trial. No commitment required!'
          : 'Unlock the ability to search all courses and more!'}
      </p>
      <div className="mx-auto mt-8 max-w-md">
        <form onSubmit={handleRedeemCode} className="bg-white/95 rounded-2xl p-5 ring-1 ring-gray-900/10 shadow">
          <label htmlFor="tiu-access-code" className="block text-sm font-semibold text-gray-900 mb-2">
            Have an access code?
          </label>
          <div className="flex gap-2">
            <input
              id="tiu-access-code"
              name="tiu-access-code"
              type="text"
              autoComplete="off"
              data-lpignore="true"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Enter your code"
              disabled={codeStatus.busy || codeStatus.done}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <button
              type="submit"
              disabled={codeStatus.busy || codeStatus.done || !accessCode.trim()}
              className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {codeStatus.busy ? 'Checking...' : 'Apply'}
            </button>
          </div>
          {codeStatus.done && (
            <p className="mt-3 text-sm text-green-700 font-medium">
              Code accepted. You have full access - no payment needed. Taking you to the app...
            </p>
          )}
          {codeStatus.error && (
            <p className="mt-3 text-sm text-red-600">{codeStatus.error}</p>
          )}
        </form>
      </div>

      <div className="mx-auto mt-16 grid max-w-4xl grid-cols-1 lg:grid-cols-2 gap-8 items-stretch sm:mt-20">
        {/* Card Input Section */}
        <div className="bg-white rounded-3xl p-8 ring-1 ring-gray-900/10 shadow-lg sm:p-10 h-full flex flex-col justify-center">
          <h3 className="text-base font-semibold text-green-600">Payment Details</h3>
          {error && (
            <div className="mt-4 tiu-alert-error" role="alert">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name on Card
              </label>
              <input
                type="text"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                className="tiu-input"
                placeholder="John Smith"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Card Number
              </label>
              <div className="border border-gray-300 rounded-lg p-3">
                <CardNumberElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                      invalid: {
                        color: '#9e2146',
                      },
                    },
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiry Date
                </label>
                <div className="border border-gray-300 rounded-lg p-3">
                  <CardExpiryElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#424770',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                        invalid: {
                          color: '#9e2146',
                        },
                      },
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CVC
                </label>
                <div className="border border-gray-300 rounded-lg p-3">
                  <CardCvcElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#424770',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                        invalid: {
                          color: '#9e2146',
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
            <button
              type="button"
              disabled={loading}
              className={classNames(
                'w-full mt-6 py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500',
                loading ? 'opacity-50 cursor-not-allowed' : ''
              )}
              onClick={() => {
                if (currentUser?.isTrial && !startTrial) {
                  setShowConfirm(true);
                } else {
                  handleSubmit();
                }
              }}
            >
              {loading ? 'Processing...' : startTrial ? 'Begin Trial' : 'Subscribe Now'}
            </button>
            <p className="mt-4 text-sm text-gray-500 text-center">
              {startTrial 
                ? 'Cancel anytime before the 7 day trial ends and you will not be charged.'
                : `You will be charged $${SUBSCRIPTION_PRICE} per month`}
            </p>
          </form>
        </div>
        {/* Features Section - dark card with price badge */}
        <div className="relative tiu-card shadow-2xl rounded-3xl p-8 ring-1 ring-gray-900/10 sm:p-10 h-full flex flex-col justify-center items-center">
          <h3 className="text-base font-semibold text-green-400 mb-4">Premium Features</h3>
          {/* Price badge */}
          <div className="flex flex-col items-center mb-6">
            <span className="inline-block text-5xl font-extrabold text-slate-900 bg-gradient-to-r from-green-500 to-teal-400 px-8 py-3 rounded-2xl shadow-lg border-4 border-green-400 mb-2">
              ${SUBSCRIPTION_PRICE}
            </span>
            <span className="text-slate-500 text-lg font-medium">/month</span>
            {startTrial && (
              <span className="mt-2 inline-block bg-green-100 text-green-800 text-xs font-bold px-3 py-1 rounded-full border border-green-400 shadow-sm">
                7 days free trial
              </span>
            )}
          </div>
          <ul className="mt-2 space-y-4 w-full max-w-xs mx-auto">
            {premiumFeatures.map((feature, index) => (
              <li key={index} className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <CheckIcon className="h-6 w-6 text-green-400" aria-hidden="true" />
                </div>
                <p className="ml-3 text-base text-slate-900">{feature}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function UpgradeToPremium() {
  return (
    <Elements stripe={stripePromise}>
      <UpgradeToPremiumForm />
    </Elements>
  );
} 